import { useStore } from '@nanostores/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  $selectedElement,
  $styleDeltas,
  type SelectedElementPayload,
  type StyleDeltas,
} from '~/lib/stores/element-selector';
import { cssColorToHex } from '~/lib/element-selector/color-utils';
import { ColorField } from './ColorField';

interface Props {
  onPreview: (styles: Record<string, string>) => void;
  onReset: () => void;
  /** Persist the edit directly in the background — never routed to the agent. */
  onSave: (selection: SelectedElementPayload, deltas: StyleDeltas) => void | Promise<void>;
  /** Upload + swap for a selected <img>. */
  onReplaceImage: (file: File) => Promise<void> | void;
}

/** panel field → css camelCase (for live preview) + kebab (for deltas/prompt) */
type Field = {
  group: string;
  label: string;
  camel: string;
  kebab: string;
  kind: 'px' | 'select' | 'color' | 'pct';
};

// Master list of every editable prop. Drives the live-preview builder in
// `update` — so text colour + font size must live here even though they render
// as dedicated everyday controls rather than in the Advanced list below.
const FIELDS: Field[] = [
  { group: 'Color', label: 'Text', camel: 'color', kebab: 'color', kind: 'color' },
  { group: 'Text', label: 'Font size', camel: 'fontSize', kebab: 'font-size', kind: 'px' },
  { group: 'Text', label: 'Font weight', camel: 'fontWeight', kebab: 'font-weight', kind: 'select' },
  { group: 'Text', label: 'Alignment', camel: 'textAlign', kebab: 'text-align', kind: 'select' },
  { group: 'Outer space', label: 'Top', camel: 'marginTop', kebab: 'margin-top', kind: 'px' },
  { group: 'Outer space', label: 'Right', camel: 'marginRight', kebab: 'margin-right', kind: 'px' },
  { group: 'Outer space', label: 'Bottom', camel: 'marginBottom', kebab: 'margin-bottom', kind: 'px' },
  { group: 'Outer space', label: 'Left', camel: 'marginLeft', kebab: 'margin-left', kind: 'px' },
  { group: 'Inner space', label: 'Top', camel: 'paddingTop', kebab: 'padding-top', kind: 'px' },
  { group: 'Inner space', label: 'Right', camel: 'paddingRight', kebab: 'padding-right', kind: 'px' },
  { group: 'Inner space', label: 'Bottom', camel: 'paddingBottom', kebab: 'padding-bottom', kind: 'px' },
  { group: 'Inner space', label: 'Left', camel: 'paddingLeft', kebab: 'padding-left', kind: 'px' },
  { group: 'Border', label: 'Color', camel: 'borderColor', kebab: 'border-color', kind: 'color' },
  { group: 'Border', label: 'Width', camel: 'borderWidth', kebab: 'border-width', kind: 'px' },
  { group: 'Border', label: 'Radius', camel: 'borderRadius', kebab: 'border-radius', kind: 'px' },
  { group: 'Color', label: 'Background', camel: 'backgroundColor', kebab: 'background-color', kind: 'color' },
  { group: 'Color', label: 'Opacity', camel: 'opacity', kebab: 'opacity', kind: 'pct' },
];

// Everything except the two "everyday" controls (text colour + font size) lives
// under the collapsed Advanced section so the default panel stays calm.
const ADVANCED_FIELDS = FIELDS.filter((f) => f.camel !== 'color' && f.camel !== 'fontSize');

const WEIGHTS = ['300', '400', '500', '600', '700', '800'];
const ALIGNS = ['left', 'center', 'right', 'justify'];

/**
 * Value to feed the live inline preview (`element.style[prop] = value`).
 * A CSS length with no unit is silently ignored on assignment — `style.fontSize
 * = '30'` no-ops — so px edits (size, margins, padding, border) never previewed
 * live even though the saved Tailwind class applied them (hence "only after
 * refresh"). Give bare numbers a `px` unit; leave united/keyword/colour values
 * as-is.
 */
function previewValue(field: Field, raw: string): string {
  const v = raw.trim();

  if (field.kind === 'px' && /^-?\d+(\.\d+)?$/.test(v)) {
    return `${v}px`;
  }

  return v;
}

// Calm, universal text-colour swatches — neutral first, then a few gentle
// accents. Not Dunea-branded: these tint the customer's own site.
const TEXT_SWATCHES: Array<{ name: string; hex: string }> = [
  { name: 'White', hex: '#FFFFFF' },
  { name: 'Ink', hex: '#0A0A0A' },
  { name: 'Grey', hex: '#737373' },
  { name: 'Blue', hex: '#6AB8F2' },
  { name: 'Green', hex: '#6EE7A6' },
  { name: 'Amber', hex: '#F2C46A' },
  { name: 'Red', hex: '#F26A6A' },
  { name: 'Lime', hex: '#D9F26A' },
];

export function ElementStylePanel({ onPreview, onReset, onSave, onReplaceImage }: Props) {
  const selection = useStore($selectedElement);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [replacing, setReplacing] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');

  // Subscribed here (not after the early return below) so every hook in this
  // component runs on every render regardless of `selection` — reading it only
  // once we know `selection` is truthy would make the hook call conditional
  // and break React's rules of hooks across selection/deselection renders.
  const deltas = useStore($styleDeltas);
  const [values, setValues] = useState<Record<string, string>>({});
  const previewRef = useRef<ReturnType<typeof setTimeout>>();
  const saveTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const savedFlagRef = useRef<ReturnType<typeof setTimeout>>();

  // re-seed local values whenever the selection changes
  useEffect(() => {
    clearTimeout(previewRef.current);
    clearTimeout(saveTimerRef.current);
    clearTimeout(savedFlagRef.current);
    setValues(selection ? { ...selection.computedStyles } : {});
    setSaveState('idle');
    setShowAdvanced(false);

    return () => {
      clearTimeout(previewRef.current);
      clearTimeout(saveTimerRef.current);
      clearTimeout(savedFlagRef.current);
    };
  }, [selection]);

  const advancedGroups = useMemo(() => {
    const byGroup = new Map<string, Field[]>();

    for (const field of ADVANCED_FIELDS) {
      byGroup.set(field.group, [...(byGroup.get(field.group) ?? []), field]);
    }

    return [...byGroup.entries()];
  }, []);

  if (!selection) {
    return null;
  }

  const sel = selection;
  const currentColorHex = cssColorToHex(values.color ?? '')?.toLowerCase() ?? null;

  // Persist the current deltas to the source file on an explicit Save click.
  // onSave (in Preview) reloads the preview afterwards so the saved Tailwind
  // class is actually applied in the iframe — the inline live-preview alone
  // doesn't survive deselect, so a reload is what makes the change stick.
  const commitSave = async () => {
    const pending = $styleDeltas.get();

    if (Object.keys(pending).length === 0) {
      return;
    }

    setSaveState('saving');

    try {
      await onSave(sel, pending);
    } finally {
      setSaveState('saved');
      clearTimeout(savedFlagRef.current);
      savedFlagRef.current = setTimeout(() => setSaveState('idle'), 1600);
    }
  };

  const update = (camel: string, kebab: string, next: string) => {
    const was = sel.computedStyles[camel] ?? '';
    setValues((prev) => ({ ...prev, [camel]: next }));

    const nextDeltas: StyleDeltas = { ...$styleDeltas.get() };

    if (next === was) {
      delete nextDeltas[kebab];
    } else {
      nextDeltas[kebab] = { value: next, was };
    }

    $styleDeltas.set(nextDeltas);

    // Live inline preview (fast), then a debounced background save.
    clearTimeout(previewRef.current);
    previewRef.current = setTimeout(() => {
      const styles: Record<string, string> = {};

      for (const field of FIELDS) {
        const delta = nextDeltas[field.kebab];

        if (!delta) {
          continue;
        }

        const value = previewValue(field, delta.value);
        styles[field.camel] = value;

        // Gradient/clipped text (hero titles etc.) ignores inline `color`
        // because its fill is transparent — mirror the colour into
        // -webkit-text-fill-color so the live preview matches the saved class.
        if (field.camel === 'color') {
          styles.webkitTextFillColor = value;
        }
      }

      onPreview(styles);
    }, 80);
  };

  const replaceImage = async (file: File | undefined) => {
    if (!file || replacing) {
      return;
    }

    setReplacing(true);

    try {
      await onReplaceImage(file);
    } finally {
      setReplacing(false);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const renderField = (field: Field) => (
    <label key={field.camel} className="mb-1 flex items-center justify-between gap-2">
      <span className="text-secondary">{field.label}</span>
      {field.kind === 'select' ? (
        <select
          value={values[field.camel] ?? ''}
          onChange={(event) => update(field.camel, field.kebab, event.target.value)}
          className="w-24 rounded-md bg-raised border border-hairline px-1.5 py-1 text-primary"
        >
          {(field.camel === 'fontWeight' ? WEIGHTS : ALIGNS).map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      ) : field.kind === 'color' ? (
        <ColorField value={values[field.camel] ?? ''} onChange={(css) => update(field.camel, field.kebab, css)} />
      ) : (
        <input
          type="text"
          value={values[field.camel] ?? ''}
          onChange={(event) => update(field.camel, field.kebab, event.target.value)}
          className="w-16 rounded-md bg-raised border border-hairline px-1.5 py-1 text-right font-geist-mono text-primary"
        />
      )}
    </label>
  );

  return (
    // A bottom sheet on phones — a 256px side panel covered ~70% of the preview being edited.
    <div className="absolute left-0 right-0 bottom-0 max-h-[55%] overflow-y-auto rounded-t-xl border-t border-hairline bg-surface p-3 text-xs z-40 md:left-auto md:top-0 md:max-h-none md:w-64 md:rounded-none md:border-t-0 md:border-l">
      {/* what's selected */}
      <div className="mb-3 truncate rounded-lg bg-raised border border-hairline px-2.5 py-1.5 font-geist-mono text-secondary">
        {sel.tag} · {sel.file.split('/').pop()}:{sel.line}
      </div>

      {sel.editableText && (
        <div className="mb-3 rounded-lg border border-hairline bg-raised px-2.5 py-1.5 text-secondary">
          Click the text in the preview and just type to change it.
        </div>
      )}

      {sel.tag === 'img' && (
        <div className="mb-4">
          <div className="mb-1 ui-caption">Image</div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(event) => replaceImage(event.target.files?.[0])}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={replacing}
            className="ui-btn-ghost w-full"
          >
            {replacing ? 'Replacing…' : 'Replace image…'}
          </button>
        </div>
      )}

      {/* everyday: text colour — the big, obvious control */}
      <div className="mb-4">
        <div className="mb-1.5 ui-caption">Text color</div>
        <div className="flex flex-wrap gap-1.5">
          {TEXT_SWATCHES.map((swatch) => {
            const active = currentColorHex === swatch.hex.toLowerCase();

            return (
              <button
                key={swatch.hex}
                type="button"
                title={swatch.name}
                aria-label={`Text color ${swatch.name}`}
                onClick={() => update('color', 'color', swatch.hex)}
                className={
                  'h-6 w-6 touch:h-8 touch:w-8 rounded-full border transition-transform can-hover:hover:scale-110 ' +
                  (active ? 'border-accent ring-2 ring-accent/60' : 'border-hairline')
                }
                style={{ backgroundColor: swatch.hex }}
              />
            );
          })}
        </div>
        <div className="mt-2 flex items-center justify-between gap-2">
          <span className="text-secondary">Custom</span>
          <ColorField value={values.color ?? ''} onChange={(css) => update('color', 'color', css)} />
        </div>
      </div>

      {/* everyday: font size */}
      <div className="mb-4">
        <div className="mb-1 ui-caption">Size</div>
        <label className="flex items-center justify-between gap-2">
          <span className="text-secondary">Font size</span>
          <input
            type="text"
            value={values.fontSize ?? ''}
            onChange={(event) => update('fontSize', 'font-size', event.target.value)}
            className="w-16 rounded-md bg-raised border border-hairline px-1.5 py-1 text-right font-geist-mono text-primary"
          />
        </label>
      </div>

      {/* everything else, out of the way */}
      <button
        type="button"
        onClick={() => setShowAdvanced((v) => !v)}
        className="mb-2 flex w-full items-center justify-between rounded-md px-1 py-1 text-secondary hover:text-primary"
      >
        <span className="ui-caption">Advanced</span>
        <span className={showAdvanced ? 'i-ph:caret-up' : 'i-ph:caret-down'} />
      </button>

      {showAdvanced &&
        advancedGroups.map(([group, fields]) => (
          <div key={group} className="mb-3">
            <div className="mb-1 ui-caption">{group}</div>
            {fields.map(renderField)}
          </div>
        ))}

      {/* save status */}
      <div className="mt-2 h-4 text-secondary">
        {saveState === 'saving' && <span>Saving…</span>}
        {saveState === 'saved' && <span className="text-success">Saved ✓</span>}
        {saveState === 'idle' && Object.keys(deltas).length === 0 && <span>Pick a value, then Save.</span>}
      </div>

      {/* explicit Save — persists the deltas and reloads the preview so the
          saved class actually shows (the inline preview alone reverts on deselect) */}
      {Object.keys(deltas).length > 0 && (
        <div className="mt-2 flex flex-col gap-2">
          <button
            type="button"
            onClick={commitSave}
            disabled={saveState === 'saving'}
            className="ui-btn-primary w-full"
          >
            {saveState === 'saving' ? 'Saving…' : 'Save changes'}
          </button>
          <button
            type="button"
            onClick={() => {
              clearTimeout(previewRef.current);
              clearTimeout(saveTimerRef.current);
              $styleDeltas.set({});
              setValues({ ...sel.computedStyles });
              onReset();
            }}
            className="ui-btn-ghost w-full"
          >
            Undo changes
          </button>
        </div>
      )}
    </div>
  );
}
